<template>
  <div>
    <div class="ImageSlider_vtoButton___6nh1">
      <FimButton
        type="secondary"
        :disabled="isButtonDisabled"
        class="w-full"
        data-testid="virtual-try-on-button"
        @click="openVirtualWindow"
      >
        <template #icon>
          <LazyIconFielmannWebcam class="size-6" />
        </template>
        {{ $t('vto.button_label') }}
      </FimButton>
    </div>
    <div id="vto-fitting-box-header" class="hidden">
      <p id="vto-fitting-box-headline" class="pl-4 md:text-lg">
        {{ $t('vto.container_headline') }}
      </p>
      <LogoSvg
        id="vto-fitting-box-logo"
        class="h-7 w-[4.875rem] md:w-[5.25rem]"
      />
    </div>
    <ClientOnly>
      <Teleport defer to="#fitmix-overlay">
        <FadeInTransition>
          <div
            v-show="isFitMixOpen"
            class="fixed right-0 top-0 z-[100] flex h-dvh w-full overscroll-contain bg-black/50 transition-opacity lg:size-full"
          >
            <div
              class="relative m-auto grid size-full grid-cols-1 grid-rows-1 overflow-y-auto bg-shade-200 sm:mx-4 sm:my-16 sm:h-[calc(100vh-8rem)] sm:max-h-[876px] sm:rounded-2xl md:mx-auto md:w-[calc(100vw-16rem)] lg:max-w-[1250px]"
            >
              <div
                class="relative col-start-1 row-start-1 grid size-full grid-cols-1 grid-rows-2 transition-opacity lg:flex"
                :class="{ 'pointer-events-none opacity-0': !showIntro }"
              >
                <FimPicture
                  provider="local"
                  class="aspect-square block size-full"
                  layout="raw"
                  object-fit="cover"
                  object-position="center"
                  mobile="/img/vto/vto-image-mobile.jpg"
                  desktop="/img/vto/vto-image-desktop.jpg"
                  alt=""
                  aria-hidden
                />
                <div
                  class="flex size-full flex-col items-center justify-start gap-2 bg-shade-200 py-7 lg:max-w-[26rem] lg:py-20"
                >
                  <Headline
                    tag="h2"
                    weight="medium"
                    class="text-3xl lg:text-4xl"
                    >{{ $t('vto.intro_headline') }}</Headline
                  >
                  <Headline
                    tag="h3"
                    weight="medium"
                    class="text-2xl lg:text-3xl"
                    >{{ $t('vto.intro_subline') }}</Headline
                  >
                  <LazyIconFielmannFrame
                    class="mt-16 hidden size-14 lg:block"
                  />
                  <p
                    v-dompurify-html="$t('vto.intro_text')"
                    class="p-4 text-center text-xl lg:pt-0"
                  ></p>
                  <FimButton
                    data-testid="vto-intro"
                    type="primary"
                    @click="onIntroCta"
                  >
                    {{ $t('vto.intro_button_label') }}
                    <template #iconAfter>
                      <LazyIconFielmannArrowRight class="size-4" />
                    </template>
                  </FimButton>
                </div>
                <button
                  data-testid="close-button"
                  class="absolute right-0 top-0 cursor-pointer p-4"
                  :aria-label="$t('modal.close')"
                  @click="onClose"
                >
                  <LazyIconFielmannClose class="size-6" />
                </button>
              </div>
              <div
                class="relative col-start-1 row-start-1 flex size-full flex-col transition-opacity"
                :class="{ 'pointer-events-none opacity-0': showIntro }"
              >
                <!--Header Section -->
                <div
                  class="sticky top-0 z-50 flex h-14 w-full items-center justify-between bg-shade-200/0 p-4 lg:absolute lg:left-0 lg:bg-transparent"
                >
                  <Headline
                    look="h4"
                    tag="div"
                    :is-uppercase="false"
                    class="text-black md:text-2xl"
                    weight="medium"
                    data-testid="product-name"
                  >
                    {{ $t('vto.container_headline') }}
                  </Headline>
                  <button
                    data-testid="close-button"
                    class="cursor-pointer"
                    :aria-label="$t('modal.close')"
                    @click="onClose"
                  >
                    <LazyIconFielmannClose class="size-6" />
                  </button>
                </div>
                <!-- Camera Content and Additional Elements -->
                <div
                  class="relative flex size-full flex-col lg:flex-row"
                  data-allow-mismatch
                >
                  <!-- Camera Overlay -->
                  <div class="sticky top-0 z-10 size-full lg:relative">
                    <div
                      id="fitmix-container"
                      ref="fitmix-container"
                      class="size-full min-h-[420px]"
                    ></div>
                    <div
                      v-if="!fitMixTermsVisible"
                      class="absolute inset-x-8 bottom-8 flex flex-col items-center justify-center gap-4"
                    >
                      <BadgeFtPrismaDesign
                        v-if="!showIntro && variantFrameSize"
                        size="large"
                        type="info"
                      >
                        <div
                          class="flex items-center gap-1 text-xs font-medium normal-case text-black md:text-base"
                        >
                          <span>
                            {{
                              pdValues
                                ? $t('vto.badge.displayed_size', {
                                    size: variantFrameSize,
                                  })
                                : $t('vto.badge.virtual_size_deviation')
                            }}
                          </span>
                          <div v-if="!pdValues" class="relative z-[150]">
                            <PdTool
                              btn-id="vto-pd-tool"
                              btn-type="link"
                              :btn-label="$t('vto.pd_scan_label')"
                              :btn-is-small="true"
                              :btn-class="'text-xs md:text-base'"
                              btn-no-padding
                            />
                          </div>
                        </div>
                      </BadgeFtPrismaDesign>
                      <FimButton
                        data-testid="take-screenshot"
                        type="primary"
                        theme="dark"
                        class=""
                        :disabled="!fitMixReady || isScreenshotInProgress"
                        @click="onScreenshot"
                      >
                        {{ $t('vto.screenshot') }}
                        <template #icon>
                          <LazyIconFielmannScreenshot class="mr-2 size-6" />
                        </template>
                      </FimButton>
                    </div>
                  </div>
                  <div class="flex h-fit w-full bg-shade-200 lg:max-w-[26rem]">
                    <div
                      class="flex size-full shrink-0 flex-col gap-6 px-8 py-6 lg:pt-20"
                    >
                      <div class="flex justify-between">
                        <Headline
                          look="h1"
                          tag="h3"
                          class="!block md:text-2xl"
                          weight="medium"
                          data-testid="product-name"
                        >
                          {{ productName }}
                        </Headline>
                        <WishlistToggle
                          :key="currentColorProduct.id"
                          :product="currentColorProduct"
                          :variant="currentVariant"
                        />
                      </div>

                      <div>
                        <div>
                          <b>{{ $t('pdp.color_heading') }}:</b>
                          {{ productColor }}
                        </div>

                        <HorizontalItemsScroller
                          ref="slider"
                          :start-at-index="0"
                          :slide-width="24"
                          :has-gradient="false"
                        >
                          <ScrollerItem
                            v-for="(entry, i) in colors"
                            :key="i"
                            class="sm:aspect-w-24 group w-fit shrink-0 snap-start sm:aspect-h-8"
                          >
                            <FimButton
                              type="raw"
                              :aria-label="entry.colorLabel"
                              no-padding
                              class="mr-2 h-6 overflow-hidden rounded-xl border border-shade-400 transition-all"
                              :class="isSelectedColor(entry) ? 'w-10' : 'w-6'"
                              @click="() => onChangeFrame(entry)"
                            >
                              <component
                                :is="entry.colorIcon"
                                class="relative size-14"
                                preserveAspectRatio="none"
                              />
                            </FimButton>
                          </ScrollerItem>
                        </HorizontalItemsScroller>
                      </div>
                      <div v-if="isRx">
                        <div>
                          <b>{{ $t('vto.lens_color') }}:</b>
                          {{
                            lensColors.find(
                              (color) => color.value === currentLensColor,
                            )?.label
                          }}
                        </div>

                        <HorizontalItemsScroller
                          :start-at-index="0"
                          :slide-width="24"
                          :has-gradient="false"
                        >
                          <ScrollerItem
                            v-for="(entry, i) in lensColors"
                            :key="i"
                            class="sm:aspect-w-24 group w-fit shrink-0 snap-start sm:aspect-h-8"
                          >
                            <FimButton
                              type="raw"
                              :aria-label="entry.label"
                              no-padding
                              class="mr-2 h-6 overflow-hidden rounded-xl border border-shade-400 transition-all"
                              :class="
                                entry.value === currentLensColor
                                  ? 'w-10'
                                  : 'w-6'
                              "
                              @click="
                                () =>
                                  onChangeLensColor(
                                    entry.value as FitMixLensMaterial,
                                  )
                              "
                            >
                              <component
                                :is="entry.colorIcon"
                                class="relative size-14"
                                preserveAspectRatio="none"
                              />
                            </FimButton>
                          </ScrollerItem>
                        </HorizontalItemsScroller>
                      </div>
                      <hr class="hidden lg:visible" />
                      <ProductPriceFtPrismaDesign
                        size="2xl"
                        :price="price"
                        no-margin
                        :applied-reductions="price.appliedReductions"
                        :show-price-from="showPriceFrom"
                        :show-price-reduction-badge="hasSpecial"
                        :show-discount="true"
                        data-testid="product-price"
                      />
                      <FimButton
                        id="vto-add-to-card"
                        data-testid="vto-add-to-card"
                        type="primary"
                        class="mb-0 w-full"
                        :disabled="
                          !isProductAvailable(currentColorProduct) ||
                          !isVariantAvailable(
                            currentColorProduct,
                            currentVariant,
                          )
                        "
                        @click="onVtoAction"
                      >
                        {{
                          isRx
                            ? $t('pdp.correctional_glasses.now_choose_glasses')
                            : $t('pdp.add_label')
                        }}
                        <template #iconAfter>
                          <component
                            :is="
                              isRx
                                ? 'LazyIconFielmannArrowRight'
                                : 'LazyIconFielmannCart'
                            "
                            class="mr-2 size-6"
                          />
                        </template>
                      </FimButton>
                    </div>
                  </div>
                </div>
                <SyteSimilarItemsScroller
                  v-if="currentVariant && !showIntro"
                  :variant-id="currentVariant.id"
                  @item-click="setCurrentProductFromRecommendation"
                />
              </div>
            </div>
          </div>
        </FadeInTransition>
      </Teleport>
    </ClientOnly>
  </div>
</template>
<script setup lang="ts">
import type { Price, Product, Variant } from '@scayle/storefront-nuxt'
import { capitalize } from 'radash'
import { useGlasses } from '~/composables/pdp/useGlasses'
import { useStorage } from '@vueuse/core'
import slugify from 'slugify'
import type {
  FitMixInstance,
  FitMixParameters,
  FitMixLensMaterial,
} from '~/types/fitmix'
import useUiState from '~/composables/ui/useUiState'
import { useVTOAvailability } from '~/composables/useVTOAvailability'

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  variant: {
    type: Object as PropType<Variant>,
    default: () => undefined,
  },
})

const {
  isChecking,
  isAvailable,
  firstAvailableVariant,
  firstAvailableEan,
  fetchAvailability,
  getEan,
} = useVTOAvailability()

const $emit = defineEmits<{
  (
    e: 'onVtoAction' | 'onVtoChangeFrame',
    value: { product: Product; variant: Variant | undefined },
  ): void
  (e: 'onVtoClose' | 'onVtoOpen'): void
}>()

const instance = useNuxtApp()
const { $currentShop, $featureToggle, $i18n, $fittingBox } = instance
const { isRxConfiguratorEnabled } = $featureToggle
const { openFitMix, isFitMixOpen, closeFitMix, isRxConfiguratorOverlayOpen } =
  useUiState()

const scope = effectScope()
onScopeDispose(() => scope.stop())

const container = useTemplateRef('fitmix-container')

const currentProduct = ref<Product>(props.product)
const currentVariant = ref<Variant | undefined>(props.variant)

const currentColorProduct = ref<Product>(currentProduct.value)

const isRx = isCorrectionalGlasses(currentProduct.value)
const { productColor } = useGlasses(currentColorProduct)
const { prescriptionValues } = useRxPrescription()

// initialization dependencies
const scriptReady = computed(() => $fittingBox.scriptReady.value)
const containerReady = ref(false)

const fitMixTermsVisible = ref(false)
const fitMixReady = ref(false)
const fitMixCreated = ref(false)
const fitmixInstance = ref<FitMixInstance>()

const activeEan = ref(
  currentVariant.value ? getVirtualTryOnEan(currentVariant.value) : undefined,
)

const vtoApiKey = computed(() => $fittingBox.vtoApiKey.value)

const showIntro = useStorage('vto-intro', true, window?.sessionStorage, {
  initOnMounted: true,
})

const { getCombinedProductPrice } = await useProductPrice()
const {
  trackVTOFittingBoxOpen,
  trackVTOFittingBoxClose,
  trackCTAClick,
  trackSelectItem,
  trackRadioButtonClick,
  trackVTOFittingBoxScreenshot,
} = await useTrackingEvents()

const { name: productName } = useProductBaseInfo(currentColorProduct)

const rxConfiguratorEnabled = computed(() =>
  isRxConfiguratorEnabled(currentProduct.value as Product),
)

const hasSpecial = computed(() =>
  Boolean(!props.variant && price.value?.appliedReductions?.length),
)

const showPriceFrom = computed(
  () =>
    currentProduct.value.priceRange?.min.withTax !==
      currentProduct.value.priceRange?.max.withTax || hasSpecial.value,
)

const price = computed(() => {
  if (rxConfiguratorEnabled.value && currentProduct.value) {
    return toValue(
      getCombinedProductPrice(currentProduct.value, currentVariant.value),
    ) as Price
  }
  if (currentVariant.value) {
    return getPrice(currentVariant.value)
  }

  try {
    return getLowestPrice(currentProduct.value)
  } catch {
    return currentProduct.value?.priceRange?.max ?? ({} as Price)
  }
})

const colors = computed(() => {
  const main = getColorSliderEntry(currentProduct.value)

  const siblingEntries = (currentProduct.value.siblings ?? [])
    .map((sibling: Product) => getColorSliderEntry(sibling))
    .filter(Boolean)

  return [main, ...siblingEntries].filter((entry) => !!entry)
})

const filename = computed(
  () => `${productName.value}-${productColor.value}.png`,
)

const getColorSliderEntry = (product: Product) => {
  const color = getFirstAttributeValue(product.attributes, 'searchColorEcom')
  const variant = currentVariant.value
    ? findMatchingVariant(product, currentVariant.value)
    : undefined
  const glassesId = variant && getVirtualTryOnEan(variant)

  if (!product.isActive || !color || !glassesId || !variant) {
    return
  }

  return {
    variant,
    product,
    colorLabel: color.label,
    colorIcon: resolveComponent(
      `LazyIconColor${capitalize(slugify(color.value || ''))}`,
    ),
    glassesId,
  }
}

const isScreenshotAllowed = ref(false)
const isScreenshotInProgress = ref(false)

const handleSnapshot = (data: { dataUrl: string }) => {
  if (!isScreenshotAllowed.value) {
    return
  }
  const link = document.createElement('a')
  link.href = data.dataUrl
  link.download = filename.value
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  // Reset screenshot states
  isScreenshotAllowed.value = false
  isScreenshotInProgress.value = false
}

const initialize = () => {
  if (import.meta.server) {
    return
  }

  if (!scriptReady.value || !vtoApiKey.value || !container.value) {
    return
  }

  // Use the available ean if it exists
  const vtoEan = getEan(activeEan)
  if (!vtoEan) {
    return
  }

  const params: FitMixParameters = {
    apiKey: vtoApiKey.value,
    lang: getIsoLanguage(toValue($currentShop).locale),
    frame: vtoEan,
    uiConfiguration: {
      apiKeyIssueMessage: true,
      frameIssueMessage: true,
      liveCameraAccessDenied: true,
      liveDetectionFailure: true,
      liveLensesAnimations: true,
      liveLensesAnimationsActive: true,
      livePhotoButton: false,
      liveRelaunchDetectionButton: false,
      loadingIndicator: true,
      networkIssueMessage: true,
      photoLiveButton: false,
      photoRenderError: false,
      photoWelcomeScreen: true,
    },
    onSnapshot: handleSnapshot,
    onStopVto: () => {
      closeFitMix()
      resetProduct()
    },
  }

  try {
    fitMixCreated.value = true
    fitmixInstance.value = $fittingBox.createFitMixWidget(
      container.value.id,
      params,
      (ready) => {
        fitMixReady.value = ready
      },
    )
  } catch (error) {
    console.error('[VTO] Error creating widget:', error)
    fitMixCreated.value = false
    fitMixReady.value = false
  }
}

// Start availability check immediately when component is mounted
onMounted(async () => {
  if (props.product) {
    await fetchAvailability(props.product)
  }
})

scope.run(() => {
  if (import.meta.server) {
    return
  }

  // Watch for container and script readiness
  watch(
    [container, scriptReady],
    ([newContainer, ready]) => {
      containerReady.value = Boolean(newContainer)
      if (containerReady.value && ready && !fitMixCreated.value) {
        initialize()
      }
    },
    { immediate: true },
  )

  // Watch for product and variant changes
  watch(
    [() => props.product, () => props.variant],
    async ([newProduct, newVariant]) => {
      if (newProduct) {
        await fetchAvailability(newProduct)
      }
    },
    { immediate: true },
  )

  // VTO instance is removed as soon as the rx configurator instance is created.
  // As the instance isn't mounted again, we need to re-initialize
  // if the #fitmixWidgetIframeContainer is missing
  watch(isRxConfiguratorOverlayOpen, (next, prev) => {
    if (
      prev &&
      !next &&
      !container?.value?.querySelector('#fitmixWidgetIframeContainer')
    ) {
      initialize()
    }
  })
})

onBeforeUnmount(() => {
  $fittingBox.resetSession('fitmix-container')
})

const onIntroCta = () => {
  showIntro.value = false
  $fittingBox.resetSession('fitmix-container')
}

const onScreenshot = () => {
  isScreenshotAllowed.value = true
  isScreenshotInProgress.value = true
  fitmixInstance.value?.getSnapshot()

  const variantForTracking = currentVariant.value || firstAvailableVariant.value

  if (variantForTracking) {
    trackVTOFittingBoxScreenshot({
      productDetails: currentProduct.value,
      variant: variantForTracking,
      element: { id: 'screenshot', label: 'Screenshot' },
    })
  }
}

const resetProduct = () => {
  currentProduct.value = props.product
  currentVariant.value = props.variant
  currentColorProduct.value = props.product
  activeEan.value = currentVariant.value
    ? getVirtualTryOnEan(currentVariant.value)
    : undefined
}

const onClose = () => {
  $fittingBox.resetSession('fitmix-container')
  $fittingBox.stopVto('fitmix-container')

  isScreenshotAllowed.value = false

  // Use the available variant for tracking if the current variant is not available
  const variantForTracking = currentVariant.value || firstAvailableVariant.value
  if (variantForTracking) {
    trackVTOFittingBoxClose({
      productDetails: currentProduct.value,
      variant: variantForTracking,
      element: { id: 'close-button', label: 'Schließen' },
    })
  }
}

const onChangeFrame = async (entry: (typeof colors.value)[number]) => {
  if (entry && entry.glassesId) {
    // Check availability of the new frame before setting it
    await fetchAvailability(entry.product)

    // Use the available ean if one was found
    const ean = firstAvailableEan.value || entry.glassesId
    activeEan.value = ean
    fitmixInstance.value?.setFrame(ean)

    currentColorProduct.value = entry.product
    currentVariant.value = entry.variant

    $emit('onVtoChangeFrame', {
      product: toValue(currentColorProduct.value),
      variant: toValue(currentVariant.value),
    })
    trackSelectItem(entry.product)
    trackRadioButtonClick({
      id: 'radio_frame-tinting',
      label: productColor.value,
    })
  }
}

const getFirstVtoVariant = (product: Product): Variant | undefined => {
  return product.variants?.find((variant) => {
    return Boolean(getVirtualTryOnEan(variant))
  })
}

const setCurrentProductFromRecommendation = async (productId: number) => {
  const key = `product-details-${productId}`

  const { data: fetchedProduct, error: productError } = await useProduct({
    key: `${key}-useFimProductDetails-${productId}`,
    params: {
      id: productId,
      includeSellableForFree: true,
      includeSoldOut: true,
      with: PRODUCT_WITH_GLASSES,
    },
    options: {
      immediate: true,
    },
  })

  if (fetchedProduct.value) {
    const variantWithVTO = getFirstVtoVariant(fetchedProduct.value)

    if (variantWithVTO) {
      // Check availability before setting the frame
      const isFrameAvailable = await fetchAvailability(fetchedProduct.value)

      if (isFrameAvailable) {
        currentProduct.value = fetchedProduct.value
        currentColorProduct.value = fetchedProduct.value
        currentVariant.value = variantWithVTO
        activeEan.value = getVirtualTryOnEan(variantWithVTO)

        if (fitmixInstance.value && activeEan.value) {
          fitmixInstance.value.setFrame(activeEan.value)
        }

        $emit('onVtoChangeFrame', {
          product: toValue(currentProduct.value),
          variant: toValue(currentVariant.value),
        })
      }
    } else {
      console.error('Failed to fetch product data', productError.value)
    }
  }
}

const onVtoAction = () => {
  $emit('onVtoAction', {
    product: toValue(currentColorProduct.value),
    variant: toValue(currentVariant.value),
  })
  trackCTAClick({
    productDetails: toValue(currentColorProduct),
    variant: toValue(currentVariant),
    element: {
      id: 'vto-add-to-card',
      label: isRx
        ? $i18n.t('pdp.correctional_glasses.now_choose_glasses')
        : $i18n.t('pdp.add_label'),
    },
  })

  nextTick().then(() => onClose())
}

const currentLensColor = ref<FitMixLensMaterial>('NO_TINTING')

const lensColors = [
  {
    label: $i18n.t('vto.lens_colors.no_tinting'),
    value: 'NO_TINTING',
    colorIcon: resolveComponent('LazyIconColorTransparent'),
  },
  {
    label: $i18n.t('vto.lens_colors.brown'),
    value: 'FIELMANN_Brown',
    colorIcon: resolveComponent('LazyIconColorLensBrown'),
  },
  {
    label: $i18n.t('vto.lens_colors.grey'),
    value: 'FIELMANN_Grey',
    colorIcon: resolveComponent('LazyIconColorLensGrey'),
  },
  {
    label: $i18n.t('vto.lens_colors.green'),
    value: 'FIELMANN_Pioneer',
    colorIcon: resolveComponent('LazyIconColorLensGreen'),
  },
]

const pdValues = computed(() => {
  if (prescriptionValues.value.pd?.left && prescriptionValues.value.pd?.right) {
    return prescriptionValues.value.pd.left + prescriptionValues.value.pd.right
  }
  return undefined
})

const variantFrameSize = computed(() => {
  const headWidth = getFirstAttributeValue(
    firstAvailableVariant.value?.attributes,
    'headWidth',
  )?.label

  return headWidth?.toUpperCase() || undefined
})

const openVirtualWindow = async (event: Event) => {
  if (!fitmixInstance.value || !firstAvailableEan.value) return

  // First set the frame
  fitmixInstance.value.setFrame(firstAvailableEan.value)

  $emit('onVtoOpen')

  // Use the available variant for tracking if the current variant is not available
  const variantForTracking = currentVariant.value || firstAvailableVariant.value
  if (variantForTracking) {
    trackVTOFittingBoxOpen({
      productDetails: currentProduct.value,
      variant: variantForTracking,
      element: event?.target as HTMLButtonElement,
    })
  }

  // Start VTO after frame is set
  fitmixInstance.value.startVto('live')
  if (currentLensColor.value !== 'NO_TINTING') {
    fitmixInstance.value.setLensMaterial(currentLensColor.value)
  } else {
    fitmixInstance.value.restoreLensDefaultMaterial()
  }

  if (pdValues.value) {
    fitmixInstance.value.setPupillaryDistance(pdValues.value)
  } else {
    fitmixInstance.value.setPupillaryDistance(63)
  }

  openFitMix()
}

const onChangeLensColor = (color: FitMixLensMaterial) => {
  currentLensColor.value = color
  trackRadioButtonClick({
    id: 'radio_lens-tinting',
    label: lensColors.find((c) => c.value === color)?.label ?? '',
  })
  if (color === 'NO_TINTING') {
    fitmixInstance.value?.restoreLensDefaultMaterial()
  } else {
    fitmixInstance.value?.setLensMaterial(color)
  }
}

const isButtonDisabled = computed(
  () => !fitMixReady.value || !isAvailable.value || isChecking.value,
)

const isSelectedColor = (entry: { product: Product; variant: Variant }) => {
  return (
    entry.product.id === toValue(currentColorProduct).id &&
    entry.variant.id === toValue(currentVariant)?.id
  )
}
</script>
